import { publicApi, privateApi, HMACAuth, API_CONFIG } from '../config/api';

class TradingService {
  constructor() {
    this.hmacAuth = null;
    this.initializeHMACAuth();
  }

  initializeHMACAuth() {
    const apiKey = localStorage.getItem('api_key');
    const secretKey = localStorage.getItem('secret_key');
    
    if (apiKey && secretKey) {
      this.hmacAuth = new HMACAuth(apiKey, secretKey);
    }
  }

  // Get trading pairs
  async getTradingPairs() {
    try {
      const response = await publicApi.get('/v1/pairs');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }



  // Get ticker data
  async getTicker(pair = null) {
    try {
      const url = pair ? `/market/tickers?pair=${pair}` : '/market/tickers';
      const response = await publicApi.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get order book
  async getOrderBook(pair) {
    try {
      const response = await publicApi.get(`/v1/stack/${pair}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get trade history
  async getTradeHistory(pair, limit = 50) {
    try {
      const response = await publicApi.get(`/market/trades/${pair}?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get assets info
  async getAssets() {
    try {
      const response = await publicApi.get('/public/exchange-info');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get server time
  async getServerTime() {
    try {
      const response = await publicApi.get('/public/servertime');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Private API calls (require authentication)
  
  // Place order
  async placeOrder(orderData) {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      const response = await publicApi.post('/order', {
        pair: orderData.pair,
        type: orderData.type, // 'buy' or 'sell'
        amount: orderData.amount,
        price: orderData.price,
        order_type: orderData.orderType || 'limit', // 'limit' or 'market'
      }, { headers });

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Cancel order
  async cancelOrder(orderId) {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      const response = await publicApi.delete(`/order/${orderId}`, { headers });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get user orders
  async getUserOrders(params = {}) {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      const queryParams = new URLSearchParams();
      if (params.pair) queryParams.append('pair', params.pair);
      if (params.state !== undefined) queryParams.append('state', params.state);
      if (params.operation !== undefined) queryParams.append('operation', params.operation);
      if (params.limit) queryParams.append('limit', params.limit);

      const url = `/order${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await publicApi.get(url, { headers });
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get balances
  async getBalances() {
    try {
      if (!this.hmacAuth) {
        this.initializeHMACAuth();
      }

      const headers = this.hmacAuth ? this.hmacAuth.getHeaders() : {};
      
      const response = await publicApi.get('/balance', { headers });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get balances using new API endpoint
  async getBalancesJWT() {
    try {
      const response = await privateApi.get('/v1/balance/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Alternative API calls using private API with JWT
  
  // Place order (using private API)
  async placeOrderJWT(orderData) {
    try {
      const response = await privateApi.post('/v1/exchange/', {
        pair: orderData.pair,
        operation: orderData.operation, // 0 for buy, 1 for sell
        amount: orderData.amount,
        price: orderData.price,
        type: orderData.type || 0, // 0 for limit, 1 for market
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get user orders (using private API)
  async getUserOrdersJWT(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.pair) queryParams.append('pair', params.pair);
      if (params.state !== undefined) queryParams.append('state', params.state);
      if (params.operation !== undefined) queryParams.append('operation', params.operation);
      if (params.type !== undefined) queryParams.append('type', params.type);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);

      const url = `/v1/orders/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await privateApi.get(url);
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get recent trades
  async getRecentTrades(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.pair) queryParams.append('pair', params.pair);

      const url = `/v1/recent_trades/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await privateApi.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get pairs volume
  async getPairsVolume() {
    try {
      const response = await privateApi.get('/v1/pairs_volume/');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get order book stack
  async getOrderBookStack(pair) {
    try {
      // Handle both DOT/USD and DOT-USD formats
      const formattedPair = pair.replace('/', '-');
      const response = await publicApi.get(`/v1/stack/${formattedPair}`);
      return response.data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error.response?.data || error.message;
    }
  }

  // Cancel order (using private API)
  async cancelOrderJWT(orderId) {
    try {
      const response = await privateApi.delete(`/v1/orders/${orderId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Place market order using new API
  async placeMarketOrder(orderData) {
    try {
      const response = await privateApi.post(API_CONFIG.TRADING.MARKET, orderData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Place limit order using new API
  async placeLimitOrder(orderData) {
    try {
      const response = await privateApi.post(API_CONFIG.TRADING.ORDERS, orderData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }

  // Get limit orders using new API
  async getLimitOrders(params = {}) {
    try {
      const response = await privateApi.get(API_CONFIG.TRADING.ORDERS, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  }
}

export default new TradingService(); 